import React, { useState, useCallback } from "react";

/**
 * SmartImage — orientation-aware image renderer for galleries.
 *
 * After the image's natural dimensions are known (onLoad), we decide:
 *   - landscape (w > h)  → fill the container with object-cover
 *   - portrait  (h >= w) → contain inside the container, centred, never cropped
 *
 * Container always centres the image so portraits sit naturally on a soft
 * background instead of being stretched or cropped into a landscape box.
 *
 * EXIF orientation is respected by the browser via `image-orientation: from-image`
 * (default in modern browsers since 2022), and we never apply CSS rotation.
 *
 * Usage:
 *   <SmartImage src="..." alt="..." />
 *   <SmartImage src="..." alt="..." className="img-zoom" />
 */
export default function SmartImage({
  src,
  alt = "",
  className = "",
  loading = "lazy",
  onClick,
  testid,
}) {
  // null = unknown (initial), 'landscape' | 'portrait'
  const [orientation, setOrientation] = useState(null);

  const handleLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    setOrientation(naturalWidth >= naturalHeight ? "landscape" : "portrait");
  }, []);

  const isPortrait = orientation === "portrait";

  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`smart-image-frame relative w-full h-full flex items-center justify-center overflow-hidden bg-[#1C110C] ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      <img
        src={src}
        alt={alt}
        loading={loading}
        onLoad={handleLoad}
        // image-orientation:from-image is the browser default in 2026 — restated
        // here for clarity. Never rotate via CSS transforms.
        style={{ imageOrientation: "from-image" }}
        className={
          isPortrait
            ? // Portrait: contain, never crop; let it breathe vertically.
              `max-h-full max-w-full w-auto h-auto object-contain ${className}`
            : // Landscape: fill the editorial frame.
              `w-full h-full object-cover ${className}`
        }
      />
    </div>
  );
}
